import React, { useState, useRef, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useFilters } from '../context/FilterContext';
import { API_BASE } from '../services/apiService';
import { formatDate } from '../utils/formatters';
import { Calendar, Building, Download, FileSpreadsheet, Presentation, Printer, ChevronDown, Home, Share2, Mail, MessageCircle, CheckCircle2, Upload } from 'lucide-react';

const SECTIONS = [
  { id: 'health', label: 'Health' },
  { id: 'ai-insights', label: 'AI Insights' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'settlement', label: 'Settlement' },
  { id: 'key-factors', label: 'Key Factors' },
  { id: 'prepost', label: 'Pre/Post' },
  { id: 'ailment', label: 'Ailments' },
  { id: 'maternity', label: 'Maternity' },
  { id: 'employee', label: 'Employees' },
  { id: 'utilization', label: 'SI Utilization' },
  { id: 'relationship', label: 'Relations' },
  { id: 'demographic', label: 'Demographics' },
  { id: 'hospital', label: 'Hospitals' },
  { id: 'duration', label: 'Stay Duration' },
  { id: 'rejection', label: 'Rejections' },
  { id: 'accidental', label: 'Accidental' },
  { id: 'claims-detail', label: 'Claims' },
  { id: 'renewal-premium', label: 'Renewal Premium' },
  { id: 'policy-benefits', label: 'Benefits' },
  { id: 'policy-benchmarking', label: 'Benchmarking' },
];

const Header = () => {
  const { clientInfo, filters, drillFilters } = useFilters();
  const [showExport, setShowExport] = useState(false);
  const [partnerLogo, setPartnerLogo] = useState(null);
  const [showLogoPrompt, setShowLogoPrompt] = useState(null);
  const [pptClientName, setPptClientName] = useState('');
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState();
  const imgRef = useRef(null);

  const handleCropDone = async () => {
    if (imgRef.current && crop && crop.width > 5 && crop.height > 5) {
      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      canvas.width = crop.width * scaleX;
      canvas.height = crop.height * scaleY;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => {
        if (blob) setPartnerLogo(new File([blob], 'partner_logo.png', { type: 'image/png' }));
      }, 'image/png');
    }
    // If no crop selected, use full image
    if (cropImage && (!crop || !crop.width || crop.width <= 5)) {
      const res = await fetch(cropImage);
      const blob = await res.blob();
      setPartnerLogo(new File([blob], 'partner_logo.png', { type: 'image/png' }));
    }
    setCropImage(null);
  };

  const handleLogoSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setCropImage(URL.createObjectURL(file));
      // Set initial crop area (center 80%) so user sees the drag handles
      setCrop({ unit: '%', x: 10, y: 10, width: 80, height: 80 });
    }
  };
  const [showShare, setShowShare] = useState(false);
  const [exporting, setExporting] = useState('');
  const [copied, setCopied] = useState(false);
  const [showEmailShare, setShowEmailShare] = useState(false);
  const [shareEmails, setShareEmails] = useState('');
  const [shareSending, setShareSending] = useState(false);
  const [shareSent, setShareSent] = useState(false);

  // Global paste listener for logo when modal is open
  useEffect(() => {
    if (!showLogoPrompt || partnerLogo || cropImage) return;
    const handleGlobalPaste = async (e) => {
      // Try clipboard event data first
      const items = e.clipboardData?.items;
      if (items) {
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            e.preventDefault();
            handleLogoSelect(item.getAsFile());
            return;
          }
        }
      }
      // Fallback: try navigator.clipboard.read() for production
      try {
        const clipboardItems = await navigator.clipboard.read();
        for (const clipItem of clipboardItems) {
          const imageType = clipItem.types.find(t => t.startsWith('image/'));
          if (imageType) {
            const blob = await clipItem.getType(imageType);
            handleLogoSelect(new File([blob], 'pasted-logo.png', { type: imageType }));
            return;
          }
        }
      } catch (err) {
        // Clipboard API not available or permission denied — ignore
      }
    };
    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [showLogoPrompt, partnerLogo, cropImage]); // eslint-disable-line

  const dashboardUrl = window.location.href;
  const shareText = `Claims Analytics Dashboard`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(dashboardUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareEmail = () => {
    setShowShare(false);
    setShowEmailShare(true);
    setShareEmails('');
    setShareSent(false);
  };

  const [shareError, setShareError] = useState('');
  const handleSendShareEmail = async () => {
    if (!shareEmails.trim()) return;
    setShareError('');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = shareEmails.split(',').map(e => e.trim()).filter(e => e);
    const valid = emails.filter(e => emailRegex.test(e));
    const invalid = emails.filter(e => !emailRegex.test(e));
    if (invalid.length > 0) {
      setShareError(`Invalid email: ${invalid.join(', ')}`);
      return;
    }
    if (valid.length === 0) {
      setShareError('Please enter a valid email address');
      return;
    }
    setShareSending(true);
    try {
      await fetch(`${API_BASE}/api/share-dashboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: valid, dashboardUrl, clientName: clientInfo.clientName || '' }),
      });
      setShareSent(true);
      setTimeout(() => { setShowEmailShare(false); setShareSent(false); setShareError(''); }, 2000);
    } catch (e) { setShareError('Failed to send. Please try again.'); }
    setShareSending(false);
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(`${shareText}\n${dashboardUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShowShare(false);
  };

  const [activeSection, setActiveSection] = useState('health');
  const [loadingSections, setLoadingSections] = useState(new Set());

  useEffect(() => {
    const headerHeight = 120; // approximate sticky header height
    const onScroll = () => {
      let current = 'health';
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= headerHeight + 50) current = s.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Check which sections are still loading (have animate-pulse skeleton)
  useEffect(() => {
    const check = () => {
      const loading = new Set();
      SECTIONS.forEach(s => {
        const el = document.getElementById(s.id);
        if (el && el.querySelector('.animate-pulse')) loading.add(s.id);
      });
      setLoadingSections(loading);
    };
    check();
    const interval = setInterval(check, 2000);
    return () => clearInterval(interval);
  }, []);

  const scrollTo = (id) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 100; // sticky header + nav height
      const y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    // Auto-scroll the active tab into view in the nav bar
    setTimeout(() => {
      document.getElementById(`nav-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 100);
  };

  const buildFilterParams = () => {
    const params = new URLSearchParams();
    // Include job_id so export endpoints load correct data
    const path = window.location.pathname;
    if (path.includes('/dashboard/')) {
      const jid = path.split('/dashboard/')[1]?.split('/')[0]?.split('?')[0];
      if (jid) params.set('job_id', jid);
    }
    if (filters.dateRange?.start) params.set('date_from', filters.dateRange.start);
    if (filters.dateRange?.end) params.set('date_to', filters.dateRange.end);
    if (filters.claimType && filters.claimType !== 'All') params.set('claim_type', filters.claimType);
    if (filters.claimStatus && filters.claimStatus !== 'All') params.set('claim_status', filters.claimStatus);
    if (filters.relation && filters.relation !== 'All') params.set('relation', filters.relation);
    if (filters.cityType && filters.cityType !== 'All') params.set('city_type', filters.cityType);
    const drillEntries = Object.entries(drillFilters || {});
    if (drillEntries.length > 0) params.set('drill', drillEntries.map(([k, v]) => `${k}:${v}`).join(','));
    return params.toString();
  };

  const downloadFile = async (url, filename, type) => {
    setExporting(type);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      /* export failed silently */
    }
    setExporting('');
    setShowExport(false);
  };

  const handleExportCSV = () => {
    const params = buildFilterParams();
    downloadFile(`${API_BASE}/api/export/csv?${params}`, `Claims_Data.csv`, 'csv');
  };

  const handleExportPPT = async (withInsights = false) => {
    const params = buildFilterParams();
    if (!withInsights && !partnerLogo && !pptClientName) {
      downloadFile(`${API_BASE}/api/export/ppt?${params}`, `Claims_Analytics.pptx`, 'ppt');
      return;
    }
    if (!withInsights && (partnerLogo || pptClientName)) {
      // Quick PPT with partner logo/client name — send as POST
      setExporting('ppt');
      setShowExport(false);
      try {
        const form = new FormData();
        if (partnerLogo) form.append('partner_logo', partnerLogo);
        if (pptClientName) form.append('ppt_client_name', pptClientName);
        const res = await fetch(`${API_BASE}/api/export/ppt?${params}`, { method: 'POST', body: form });
        if (!res.ok) throw new Error('Export failed');
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'Claims_Analytics.pptx';
        a.click();
        URL.revokeObjectURL(a.href);
      } catch (e) { /* */ }
      setExporting('');
      return;
    }
    // AI insights PPT — may return binary (sync) or JSON (async)
    setExporting('ppt-ai');
    setShowExport(false);
    try {
      let res;
      if (partnerLogo || pptClientName) {
        const form = new FormData();
        if (partnerLogo) form.append('partner_logo', partnerLogo);
        if (pptClientName) form.append('ppt_client_name', pptClientName);
        res = await fetch(`${API_BASE}/api/export/ppt?${params}&insights=true`, { method: 'POST', body: form });
      } else {
        res = await fetch(`${API_BASE}/api/export/ppt?${params}&insights=true`);
      }
      const contentType = res.headers.get('content-type') || '';

      if (contentType.includes('presentation')) {
        // Sync fallback — binary PPT returned directly
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `Claims_Analytics_AI.pptx`;
        a.click();
        URL.revokeObjectURL(a.href);
        setExporting('');
      } else {
        // Async — JSON with ppt_id, poll for result
        const data = await res.json();
        if (data.ppt_id) {
          const pollDownload = async (attempts = 0) => {
            if (attempts > 40) { setExporting(''); return; }
            const dlRes = await fetch(`${API_BASE}/api/export/ppt/download/${data.ppt_id}`);
            if (dlRes.ok && (dlRes.headers.get('content-type') || '').includes('presentation')) {
              const blob = await dlRes.blob();
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `Claims_Analytics_AI.pptx`;
              a.click();
              URL.revokeObjectURL(a.href);
              setExporting('');
            } else {
              setTimeout(() => pollDownload(attempts + 1), 3000);
            }
          };
          setTimeout(() => pollDownload(), 5000);
        } else { setExporting(''); }
      }
    } catch (e) { setExporting(''); }
  };

  const handlePrintPDF = () => {
    setShowExport(false);
    window.print();
  };

  return (
    <>
    {/* Export toast notification */}
    {exporting && (
      <div className="fixed top-4 right-4 z-[60] bg-purple-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-pulse">
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        <div>
          <p className="text-sm font-semibold">{exporting === 'ppt-ai' ? 'Generating AI Insights PPT...' : 'Generating PPT...'}</p>
          <p className="text-[10px] text-purple-200">{exporting === 'ppt-ai' ? 'This usually takes 30-50 seconds' : 'Please wait...'}</p>
        </div>
      </div>
    )}
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm print:shadow-none">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button onClick={() => window.location.href = '/'} className="p-1.5 rounded-lg hover:bg-purple-50 transition-colors" title="Home">
                <Home size={16} className="text-purple-600" />
              </button>
              <img src="/pazcare-logo.svg" alt="PazCare" className="h-7 cursor-pointer" onClick={() => window.location.href = '/'} />
            </div>
            <div className="hidden sm:flex items-center gap-2 text-xs text-gray-600 flex-wrap">
              <Calendar size={11} />
              <span className="font-semibold">Policy Period:</span>
              <span>{formatDate(clientInfo.policyStart)} - {formatDate(clientInfo.policyEnd)}</span>
              {clientInfo.misDate && clientInfo.misDate !== '-' && (<><span className="text-gray-300">|</span><span className="text-gray-500">MIS Date: <strong className="text-gray-700">{formatDate(clientInfo.misDate)}</strong></span></>)}
            </div>
          </div>

          {/* Share + Export buttons */}
          <div className="flex items-center gap-2 print:hidden">
            {/* Share */}
            <div className="relative" data-tour="share-btn">
              <button
                onClick={() => { setShowShare(!showShare); setShowExport(false); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
              >
                <Share2 size={13} />
              </button>
              {showShare && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowShare(false)} />
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                    <button onClick={handleCopyLink} className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors">
                      <Share2 size={13} className="text-purple-600" />
                      <span className="font-medium">{copied ? 'Copied!' : 'Copy Link'}</span>
                    </button>
                    <button onClick={handleShareEmail} className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors">
                      <Mail size={13} className="text-blue-600" />
                      <span className="font-medium">Email</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Export */}
            <div className="relative" data-tour="export-btn">
              <button
                onClick={() => { setShowExport(!showExport); setShowShare(false); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 shadow-sm transition-all"
              >
                <Download size={13} /> Export <ChevronDown size={11} />
              </button>

            {showExport && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowExport(false)} />
                <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                  {/* CSV export removed for data privacy */}
                  <button
                    onClick={() => { setShowLogoPrompt('quick'); setShowExport(false); }}
                    disabled={!!exporting}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors disabled:opacity-50"
                  >
                    <Presentation size={14} className="text-orange-500" />
                    <div className="text-left">
                      <p className="font-medium">{exporting === 'ppt' ? 'Generating...' : 'Download PPT (Quick)'}</p>
                      <p className="text-[10px] text-gray-400">Data + charts, no AI insights</p>
                    </div>
                  </button>
                  <button
                    onClick={() => { setShowLogoPrompt('ai'); setShowExport(false); }}
                    disabled={!!exporting}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors disabled:opacity-50"
                  >
                    <Presentation size={14} className="text-purple-600" />
                    <div className="text-left">
                      <p className="font-medium">{exporting === 'ppt-ai' ? 'Generating with AI...' : 'Download PPT (With AI Insights)'}</p>
                      <p className="text-[10px] text-gray-400">Data + charts + Paz Insights & Recommendations</p>
                    </div>
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={handlePrintPDF}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                  >
                    <Printer size={14} className="text-blue-500" />
                    <div className="text-left">
                      <p className="font-medium">Print / Save as PDF</p>
                      <p className="text-[10px] text-gray-400">Browser print dialog</p>
                    </div>
                  </button>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 pb-2 overflow-x-auto print:hidden" data-tour="nav-tabs">
          {SECTIONS.map(s => {
            const isLoading = loadingSections.has(s.id);
            return (
              <button
                key={s.id}
                id={`nav-${s.id}`}
                onClick={() => scrollTo(s.id)}
                className={`px-3 py-1 text-[11px] font-medium rounded-md whitespace-nowrap transition-colors ${
                  isLoading ? 'text-gray-300 bg-gray-50' :
                  activeSection === s.id ? 'text-purple-700 bg-purple-100 font-semibold' :
                  'text-gray-500 hover:text-purple-700 hover:bg-purple-50'
                }`}
              >
                {isLoading ? (
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-pulse" />
                    {s.label}
                  </span>
                ) : s.label}
              </button>
            );
          })}
        </div>
      </div>
    </header>
      {/* Email Share Modal */}
      {showEmailShare && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-2 mb-4">
              <Mail size={18} className="text-purple-600" />
              <h3 className="text-sm font-bold text-gray-900">Share Dashboard via Email</h3>
            </div>
            {shareSent ? (
              <div className="text-center py-4">
                <CheckCircle2 size={32} className="text-green-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-800">Email sent!</p>
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-3">Enter email addresses (comma-separated) to share the dashboard link</p>
                <input
                  value={shareEmails}
                  onChange={e => { setShareEmails(e.target.value); setShareError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleSendShareEmail()}
                  className={`w-full text-xs border rounded-lg px-3 py-2.5 mb-1 focus:ring-2 focus:ring-purple-300 focus:border-purple-400 ${shareError ? 'border-red-300' : 'border-gray-200'}`}
                  placeholder="email1@company.com, email2@company.com"
                  autoFocus
                />
                {shareError && <p className="text-[10px] text-red-500 mb-2">{shareError}</p>}
                {!shareError && <div className="mb-2" />}
                <div className="flex gap-2">
                  <button
                    onClick={handleSendShareEmail}
                    disabled={shareSending || !shareEmails.trim()}
                    className="flex-1 py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {shareSending ? 'Sending...' : 'Send'}
                  </button>
                  <button
                    onClick={() => setShowEmailShare(false)}
                    className="px-4 py-2.5 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Partner Logo Prompt Modal */}
      {showLogoPrompt && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50" onClick={() => setShowLogoPrompt(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-6 w-80">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Add Partner Logo?</h3>
            <p className="text-[10px] text-gray-500 mb-4">Upload your company logo to show alongside Pazcare on the PPT</p>

            {cropImage ? (
              <div className="mb-4">
                <div className="bg-gray-100 rounded-lg overflow-hidden flex justify-center" style={{ maxHeight: 220 }}>
                  <ReactCrop crop={crop} onChange={c => setCrop(c)} >
                    <img ref={imgRef} src={cropImage} alt="Crop" style={{ maxHeight: 220 }} />
                  </ReactCrop>
                </div>
                <p className="text-[9px] text-gray-400 mt-1 text-center">Drag the corners or edges to resize crop area</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={handleCropDone} className="flex-1 py-1.5 text-xs font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700">{crop?.width ? 'Crop & Use' : 'Use Full Image'}</button>
                  <button onClick={() => setCropImage(null)} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                </div>
              </div>
            ) : partnerLogo ? (
              <div className="flex items-center gap-2 mb-4 p-2 bg-purple-50 rounded-lg border border-purple-200">
                <img src={URL.createObjectURL(partnerLogo)} alt="Logo" className="h-8 object-contain" />
                <span className="text-xs text-gray-700 flex-1 truncate">{partnerLogo.name || 'Cropped logo'}</span>
                <button onClick={() => setPartnerLogo(null)} className="text-xs text-red-400 hover:text-red-600">✕</button>
              </div>
            ) : (
              <div
                className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/30 transition-all mb-4"
                onClick={() => document.getElementById('partner-logo-input').click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-purple-400', 'bg-purple-50/30'); }}
                onDragLeave={e => { e.currentTarget.classList.remove('border-purple-400', 'bg-purple-50/30'); }}
                onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-purple-400', 'bg-purple-50/30'); const f = e.dataTransfer.files[0]; if (f) handleLogoSelect(f); }}
                onPaste={e => { const items = e.clipboardData?.items; if (items) { for (const item of items) { if (item.type.startsWith('image/')) { handleLogoSelect(item.getAsFile()); break; } } } }}
                tabIndex={0}
              >
                <Upload size={20} className="mx-auto mb-1 text-purple-400" />
                <p className="text-[11px] text-gray-500">Upload, drag & drop, or paste logo</p>
                <p className="text-[9px] text-gray-400">PNG / JPG - you can crop after selecting</p>
                <input id="partner-logo-input" type="file" accept=".png,.jpg,.jpeg,.svg" className="hidden" onChange={e => { if (e.target.files[0]) handleLogoSelect(e.target.files[0]); }} />
              </div>
            )}

            <div className="mb-3">
              <label className="block text-[10px] font-semibold text-gray-500 mb-1">Client Name (for PPT footer)</label>
              <input value={pptClientName} onChange={e => setPptClientName(e.target.value)} className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300" placeholder="e.g. Paz Ltd" />
            </div>

            <button
              onClick={() => { handleExportPPT(showLogoPrompt === 'ai'); setShowLogoPrompt(null); }}
              disabled={!!exporting}
              className="w-full py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-lg hover:bg-purple-700 mb-2 disabled:opacity-50"
            >
              {exporting ? 'Generating...' : partnerLogo ? 'Generate PPT with Logo' : 'Generate PPT without Logo'}
            </button>
            <button onClick={() => setShowLogoPrompt(null)} className="w-full text-xs text-gray-400 hover:text-gray-600">Cancel</button>
          </div>
        </>
      )}
    </>
  );
};

export default Header;
